<!-- src/components/Modal.vue -->
<template>
  <div v-if="isOpen" class="modal-backdrop">
    <div class="modal-container" @click.stop>
      <div class="modal-content">
        <!-- 内容 -->
        <div class="container">
          <!-- エラーメッセージの表示エリア -->
          <div
            v-if="errorMessage"
            class="error-message"
            style="white-space: pre-wrap"
          >
            {{ errorMessage }}
          </div>
          <div class="form-group row">
            <label class="required-mark">※</label>
            <label class="title">エージェントセグメント名</label>
            <input
              id="AgSegmentName"
              v-model="agSegmentName"
              type="text"
              placeholder="エージェントセグメント名"
              maxlength="255"
              class="form-control"
            />
          </div>
          <div class="form-group row">
            <label class="required-mark">※</label>
            <div class="sub-container">
              <div class="sub-row">
                <label class="title">所属エージェント</label>
                <button
                  type="button"
                  name="btnUpload"
                  class="btn btn-primary"
                  @click="onUpload"
                >
                  アップロード
                </button>
                <input
                  type="file"
                  ref="fileInput"
                  @change="handleFileUpload"
                  style="display: none"
                  accept=".csv"
                />
              </div>
              <div class="sub-row">
                <label class="title">アップロードファイル名</label>
                <span>{{ uploadFileName }}</span>
              </div>
              <div class="sub-row">
                <label class="title">取込件数</label>
                <span>{{ updateDataCnt }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="form-inline flex-container">
        <button
          type="button"
          name="btnBack"
          class="btn btn-footer btn-primary"
          @click="onBack"
        >
          戻る
        </button>
        <button
          type="button"
          name="btnRegister"
          class="btn btn-footer btn-primary"
          @click="onRegister"
        >
          登録
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, watch, ref } from "vue";
import { useProcessingStore } from "../../store/useProcessingStore";
import apiAuthClient from "@/util/apiClientAuth";
import { AppConstants } from "@/constants/AppConstants";
import {
  formatMessage,
  sanitizeInput,
  hasNoGaiji,
  checkFileName,
  commonHandleAxiosError,
} from "@/Common";
import messages from "@/assets/messages.json";

export default defineComponent({
  name: "AgSegmentModalComponent",
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    agCode: {
      type: String,
      required: true,
    },
    userId: {
      type: String,
      required: true,
    },
    categoryId: {
      type: String,
      required: true,
    },
  },
  emits: ["closed", "registered"],
  setup(props, { emit }) {
    const processingStore = useProcessingStore();
    const errorMessage = ref("");
    const agSegmentName = ref("");
    const uploadFileName = ref("");
    const updateDataCnt = ref("");
    const fileInput = ref<HTMLInputElement | null>(null);
    const selectedFile = ref<File | null>(null);

    watch(
      () => props.isOpen,
      (newVal) => {
        if (newVal) {
          // 画面初期化
          errorMessage.value = "";
          agSegmentName.value = "";
          uploadFileName.value = "";
          updateDataCnt.value = "";
          selectedFile.value = null;
        }
      }
    );

    /**
     * アップロードボタン押下
     * アップロードボタン押下イベントであり、File属性のイベント発火
     */
    const onUpload = () => {
      // ファイル選択イベント発火
      if (fileInput.value) {
        fileInput.value.value = "";
        fileInput.value.click();
      }
    };

    /**
     * アップロードボタン押下
     * ：File属性のイベントであり、選択ファイルのチェック処理を実施
     */
    const handleFileUpload = async () => {
      if (processingStore.isProcessing) {
        // 何か処理中なら処理をしない
        return;
      }

      // 処理中フラグON
      processingStore.isProcessing = true;

      // メッセージクリア
      errorMessage.value = "";
      selectedFile.value = null;
      uploadFileName.value = "";
      updateDataCnt.value = "";

      try {
        const file = fileInput.value?.files?.[0];
        if (file) {
          // 拡張子のチェック
          if (!file.name.toLowerCase().endsWith(".csv")) {
            errorMessage.value = formatMessage(messages.ERR_0010);
            return;
          }

          // ファイル名のチェック
          if (!checkFileName(file.name.toLowerCase())) {
            errorMessage.value = formatMessage(messages.ERR_0025, "ファイル名");
            return;
          }

          // レコード数のチェック
          const recordCount = await getCsvRecordCount(file);
          if (recordCount === 0) {
            errorMessage.value = formatMessage(messages.ERR_0011);
            return;
          }

          // ファイル情報設定
          selectedFile.value = file;
          uploadFileName.value = file.name;
          updateDataCnt.value = recordCount.toLocaleString();
        }
      } finally {
        // 処理中フラグOFF
        processingStore.isProcessing = false;
      }
    };

    /**
     * アップロード処理
     * ：CSVファイルを読み込み、レコード数を取得
     */
    const getCsvRecordCount = (file: File): Promise<number> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const csvText = event.target?.result as string;
          const records = csvText
            .split(/\r\n|\n/)
            .filter((line) => line.trim() !== "");
          console.log(records.length);
          resolve(records.length);
        };
        reader.onerror = () => {
          reject(reader.error);
        };
        reader.readAsText(file);
      });
    };

    /**
     * 戻るボタン押下
     */
    const onBack = () => {
      // 確認ダイアログ表示
      const isConfirm = window.confirm(formatMessage(messages.CNF_0002));
      if (!isConfirm) {
        // いいえの場合
        return;
      }
      emit("closed");
    };

    /**
     * 登録ボタン押下
     */
    const onRegister = async () => {
      if (processingStore.isProcessing) {
        return;
      }

      // 処理中フラグON
      processingStore.isProcessing = true;

      // メッセージクリア
      errorMessage.value = "";

      try {
        // 入力チェック
        if (!validate()) {
          return;
        }

        // 確認ダイアログ表示
        const isConfirm = window.confirm(
          formatMessage(messages.CNF_0001, "エージェントセグメント", "登録")
        );
        if (!isConfirm) {
          // いいえの場合
          return;
        }

        // AGセグメントファイル取込
        if (!selectedFile.value) return;
        try {
          // API呼出し
          const formData = new FormData();
          formData.append("systemKbn", AppConstants.SYSTEM_KBN_WITH);
          formData.append("agCode", props.agCode);
          formData.append("userId", props.userId);
          formData.append("categoryId", props.categoryId);
          formData.append(
            "agSegmentData.agSegmentName",
            sanitizeInput(agSegmentName.value)
          );
          formData.append("agSegmentData.uploadFile", selectedFile.value);

          await apiAuthClient.post(
            "/api/AgSegment/create/agSegment",
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );

          // 完了メッセージ表示
          alert(
            formatMessage(messages.INF_0001, "エージェントセグメント", "登録")
          );

          emit("registered");
        } catch (error: unknown) {
          // エラー処理
          handleAxiosError(error);
        }
      } finally {
        // 処理中フラグOFF
        processingStore.isProcessing = false;
      }
    };

    /**
     * チェック処理
     */
    const validate = (): boolean => {
      // サニタイジング
      const sanitizedAgSegmentName = sanitizeInput(agSegmentName.value);

      const messageList = ref<string[]>([]);

      // エージェントセグメント名のチェック
      if (!sanitizedAgSegmentName) {
        messageList.value.push(
          formatMessage(messages.ERR_0001, "エージェントセグメント名")
        );
      } else if (!hasNoGaiji(sanitizedAgSegmentName)) {
        messageList.value.push(
          formatMessage(messages.ERR_0026, "エージェントセグメント名")
        );
      }

      // アップロードファイルのチェック
      if (!selectedFile.value) {
        messageList.value.push(
          formatMessage(messages.ERR_0003, "アップロードファイル")
        );
      } else {
        // 拡張子のチェック
        if (!uploadFileName.value.toLowerCase().endsWith(".csv")) {
          messageList.value.push(formatMessage(messages.ERR_0010));
        }
        // 取込件数
        if (updateDataCnt.value === "0") {
          messageList.value.push(formatMessage(messages.ERR_0011));
        }
      }

      // エラーがある場合
      if (messageList.value.length > 0) {
        messageList.value.forEach((message, index) => {
          if (index > 0) {
            errorMessage.value += String.fromCharCode(10);
          }
          errorMessage.value += message;
        });
        return false;
      } else {
        errorMessage.value = "";
        return true;
      }
    };

    /**
     * AxiosErrorを処理します。
     * @param error エラー情報
     */
    const handleAxiosError = (error: unknown) => {
      errorMessage.value = commonHandleAxiosError(error, true);
    };

    return {
      errorMessage,
      close,
      onUpload,
      onRegister,
      onBack,
      handleFileUpload,
      agSegmentName,
      uploadFileName,
      updateDataCnt,
      fileInput,
    };
  },
});
</script>

<style scoped>
/*
  モーダル設定
*/
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}
.modal-container {
  background: white;
  padding: 20px;
  border-radius: 5px;
  position: relative;
  min-height: 60%; /* モーダルの最大高さを設定 */
  max-height: 80vh; /* モーダルの最大高さを設定 */
  overflow-y: auto; /* モーダル内でスクロールを有効にする */
  display: flex;
  flex-direction: column;
  width: 80%; /* モーダルの幅を設定 */
}
.modal-close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 20px;
}
.modal-content {
  flex: 1; /* 残りのスペースを占める */
  border-width: 0px;
  margin: 0 auto; /* 中央寄せ */
  flex-direction: column;
  display: flex;
}
/*
  項目設定
*/
.errorform {
  margin-top: 50px;
  width: 100%;
  height: 500px;
  text-align: center;
}
label.required-mark {
  width: 20px;
  text-align: center;
  color: red;
  margin-right: 5px;
}
label.title {
  width: 250px;
  text-align: center;
  background-color: lightgrey;
  padding: 0.375rem 0.75rem;
  margin-right: 10px;
}
.form-group {
  margin: 0.25rem 0.125rem;
}
.form-control {
  display: inline-block;
  width: 50%;
}
select.form-control {
  -webkit-appearance: auto;
  appearance: auto;
}
.flex-container {
  display: flex;
  justify-content: flex-end;
}
.btn {
  margin: 0.25rem 0.125rem;
}
.btn-footer {
  margin: 0.5rem 0.55rem;
  padding: 0.375rem 2rem;
  border-radius: 1rem;
  width: 140px;
}
.container {
  border: 1px solid balck;
  padding: 10px;
}
.row {
  display: flex;
  align-items: center;
  padding: 5px 0;
}
.sub-container {
  border: 1px solid black;
  padding: 10px;
  margin-left: 20px;
  width: 100%;
}
.sub-row {
  display: flex;
  align-items: center;
  padding: 5px 0;
}
</style>
